import { gql } from '@apollo/client';
import { chakra, FormControl, FormLabel, GridItem, Stack, Text, useToast } from '@chakra-ui/react';
import { Permission } from '@tp-vision/roles-permissions';
import _ from 'lodash';
import { KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '~auth/useAuth';
import { ConfigurationStatusType } from '~components/configurations/ConfigStateToggle';
import { FormLabelPendingIndicator } from '~components/ui/FormLabelPendingIndicator';
import { PercentageInput } from '~components/ui/PercentageInput';
import { fromError } from '~utils/errors';
import { isDefined } from '~utils/types';
import { checkStateToDisableTab } from '../ConfigDetailSettings';
import {
  ConfigDisplayVolumeLimits_ConfigFragment,
  useConfigUpdateVolumeLimitMaxMutation,
  useConfigUpdateVolumeLimitMinMutation,
} from './__generated__/ConfigDisplayVolumeLimits.graphql';

interface Props {
  config: ConfigDisplayVolumeLimits_ConfigFragment;
  configStatus?: ConfigurationStatusType;
}

export function ConfigDisplayVolumeLimits({ config, configStatus }: Props) {
  const [min, isMinLoading] = useVolumeMinLimit(config);
  const [max, isMaxLoading] = useVolumeMaxLimit(config);

  const { verifyUserPermissions } = useAuth();
  const { t } = useTranslation();
  if (!min.isSupported && !max.isSupported) {
    return null;
  }

  const hasConfigUpdatePermission = !verifyUserPermissions([Permission.ConfigurationUpdate]);

  return (
    <>
      <GridItem colSpan={{ base: 8, md: 7 }} mt={10}>
        <FormLabel htmlFor="volumeLimits">
          {t('volumeLimits')}
          <FormLabelPendingIndicator isPending={isMinLoading || isMaxLoading} />
        </FormLabel>
        <Text color="gray.500" marginBottom="3">
          {t('volumeLimitsText')}
        </Text>
      </GridItem>
      <GridItem colSpan={{ base: 8, sm: 6, lg: 8, xl: 3 }}>
        <Stack direction={{ base: 'column', sm: 'row' }} spacing="8">
          {min.isSupported && (
            <FormControl maxWidth="45%">
              <FormLabel>{t('min')}</FormLabel>
              <PercentageInput
                min={0}
                {...min.getInputProps()}
                id="volumeLimits"
                isDisabled={checkStateToDisableTab(configStatus) ? true : hasConfigUpdatePermission}
              />
            </FormControl>
          )}
          {max.isSupported && (
            <FormControl maxWidth="45%">
              <FormLabel>{t('max')}</FormLabel>
              <PercentageInput
                max={100}
                {...max.getInputProps()}
                isDisabled={checkStateToDisableTab(configStatus) ? true : hasConfigUpdatePermission}
              />
            </FormControl>
          )}
        </Stack>
        {isDefined(min.error || max.error) && (
          <chakra.span color="red.500" fontSize="sm">
            {min.error ?? max.error}
          </chakra.span>
        )}
      </GridItem>
    </>
  );
}

function useVolumeMinLimit(config: ConfigDisplayVolumeLimits_ConfigFragment) {
  const maxLimit = useMemo(() => config.volume?.limits?.max?.reported, [config]);
  const minLimit = useMemo(() => config.volume?.limits?.min?.reported, [config]);
  const [minValue, setMinValue] = useState(minLimit ?? 0);
  /**
   * Store the initial value in a ref.
   * This allows us to skip submitting the mutations if the value in the input hasn't changed at all.
   */
  const previousMin = useRef(minValue);
  const { t } = useTranslation();
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    const updateValue = minLimit ?? 0;

    setMinValue(updateValue);
    previousMin.current = updateValue;
  }, [minLimit]);

  useEffect(() => {
    if (minValue > (maxLimit ?? 100)) setError('Min value exceeds the max limit.');
    else if (minValue < 0 || _.isNaN(minValue)) setError('Values below 0 are not allowed.');
    else setError(undefined);
  }, [maxLimit, minLimit, minValue]);

  const toast = useToast();
  const [updateVolumeLimitMin, { loading }] = useConfigUpdateVolumeLimitMinMutation();
  const performUpdateVolumeLimitMin = useCallback(async () => {
    if (minValue === previousMin.current) return;
    if (error) return;

    try {
      await updateVolumeLimitMin({
        variables: {
          input: {
            configurationId: config.id,
            min: minValue,
          },
        },
        refetchQueries: ['CustomerConfigDetailPage'],
        awaitRefetchQueries: true,
      });
      // The initial value has changed so update the ref.
      previousMin.current = minValue;
    } catch (err) {
      toast({
        status: 'error',
        title: t('volumeMinimumErr'),
        description: fromError(err, 'UpdateVolumeLimitMin'),
      });
    }
  }, [minValue, error, updateVolumeLimitMin, config, toast, t]);

  const getInputProps = useCallback(() => {
    return {
      value: minValue,
      onChange: (val: string) => setMinValue(Number(val)),
      onBlur: () => performUpdateVolumeLimitMin(),
      onKeyUp: (e: KeyboardEvent) => {
        if (e.key === 'Enter') {
          performUpdateVolumeLimitMin();
        }
      },
    };
  }, [minValue, performUpdateVolumeLimitMin]);

  return [
    useMemo(
      () => ({
        isSupported: !_.isNil(minLimit),
        value: minValue,
        error,
        getInputProps,
      }),
      [error, getInputProps, minLimit, minValue],
    ),
    loading,
  ] as const;
}

function useVolumeMaxLimit(config: ConfigDisplayVolumeLimits_ConfigFragment) {
  const minLimit = useMemo(() => config.volume?.limits?.min?.reported, [config]);
  const maxLimit = useMemo(() => config.volume?.limits?.max?.reported, [config]);
  const [maxValue, setMaxValue] = useState(maxLimit ?? 0);
  /**
   * Store the initial value in a ref.
   * This allows us to skip submitting the mutations if the value in the input hasn't changed at all.
   */
  const previousMax = useRef(maxValue);
  const { t } = useTranslation();

  const [error, setError] = useState<string | undefined>(undefined);

  const toast = useToast();
  const [updateVolumeLimitMax, { loading }] = useConfigUpdateVolumeLimitMaxMutation();

  useEffect(() => {
    const updateValue = maxLimit ?? 100;

    setMaxValue(updateValue);
    previousMax.current = updateValue;
  }, [maxLimit]);

  useEffect(() => {
    if (maxValue < (minLimit ?? 0)) setError('Max value is lower than the min limit.');
    else if (maxValue > 100) setError('Values above 100 are not allowed.');
    else setError(undefined);
  }, [maxLimit, maxValue, minLimit]);

  const performUpdateVolumeLimitMax = useCallback(async () => {
    if (maxValue === previousMax.current) return;
    if (error) return;

    try {
      await updateVolumeLimitMax({
        variables: {
          input: {
            configurationId: config.id,
            max: maxValue,
          },
        },
        refetchQueries: ['CustomerConfigDetailPage'],
        awaitRefetchQueries: true,
      });
      // The initial value has changed so update the ref.
      previousMax.current = maxValue;
    } catch (err) {
      toast({
        status: 'error',
        title: t('volumeMaximumErr'),
        description: fromError(err, 'UpdateVolumeLimitMax'),
      });
    }
  }, [maxValue, error, updateVolumeLimitMax, config, toast, t]);

  const getInputProps = useCallback(() => {
    return {
      value: maxValue,
      onChange: (val: string) => setMaxValue(Number(val)),
      onBlur: () => performUpdateVolumeLimitMax(),
      onKeyUp: (e: KeyboardEvent) => {
        if (e.key === 'Enter') {
          performUpdateVolumeLimitMax();
        }
      },
    };
  }, [maxValue, performUpdateVolumeLimitMax]);

  return [
    useMemo(
      () => ({
        isSupported: !_.isNil(maxLimit),
        value: maxValue,
        error,
        getInputProps,
      }),
      [error, getInputProps, maxLimit, maxValue],
    ),
    loading,
  ] as const;
}

ConfigDisplayVolumeLimits.graphql = {
  fragments: {
    ConfigDisplayVolumeLimits_config: gql`
      fragment ConfigDisplayVolumeLimits_config on ConfigurationModel {
        id
        volume {
          limits {
            min {
              reported
            }
            max {
              reported
            }
          }
        }
      }
    `,
  },
  mutations: {
    ConfigUpdateVolumeLimitMin: gql`
      mutation ConfigUpdateVolumeLimitMin($input: ConfigurationUpdateVolumeLimitMinInput!) {
        configurationUpdateVolumeLimitMin(input: $input) {
          id
          ...ConfigDisplayVolumeLimits_config
        }
      }
    `,
    ConfigUpdateVolumeLimitMax: gql`
      mutation ConfigUpdateVolumeLimitMax($input: ConfigurationUpdateVolumeLimitMaxInput!) {
        configurationUpdateVolumeLimitMax(input: $input) {
          id
          ...ConfigDisplayVolumeLimits_config
        }
      }
    `,
  },
};
